import type { WebcamResolution } from "../core/types";

export type ResolutionOption = { width: number; height: number; label: string };

export type WebcamTrackInfo = {
    capabilities: MediaTrackCapabilities | null;
    settings: MediaTrackSettings;
};

export type ProbeAspectRatio = "4:3" | "16:9" | "1:1" | "3:4" | "9:16";

export type ResolutionProbeVariant = "default" | "crop-and-scale";

export type ResolutionProbeCandidate = {
    width: number;
    height: number;
    aspectRatio: ProbeAspectRatio;
};

export type ResolutionProbeResult = {
    width: number;
    height: number;
    aspectRatio: ProbeAspectRatio;
    variant: ResolutionProbeVariant;
    success: boolean;
    actual?: Pick<
        MediaTrackSettings,
        "width" | "height" | "frameRate" | "resizeMode" | "aspectRatio"
    >;
    errorName?: string;
    errorConstraint?: string;
    errorMessage?: string;
};

const CURATED_RESOLUTIONS: ResolutionOption[] = [
    // Landscape
    { width: 3840, height: 2160, label: "4K (3840×2160)" },
    { width: 2560, height: 1440, label: "QHD (2560×1440)" },
    { width: 1920, height: 1080, label: "Full HD (1920×1080)" },
    { width: 1280, height: 720, label: "HD (1280×720)" },
    { width: 960, height: 540, label: "qHD (960×540)" },
    { width: 640, height: 480, label: "VGA (640×480)" },
    { width: 320, height: 240, label: "QVGA (320×240)" },
    // Portrait
    { width: 2160, height: 3840, label: "4K Portrait (2160×3840)" },
    { width: 1440, height: 2560, label: "QHD Portrait (1440×2560)" },
    { width: 1080, height: 1920, label: "Full HD Portrait (1080×1920)" },
    { width: 720, height: 1280, label: "HD Portrait (720×1280)" },
    { width: 540, height: 960, label: "qHD Portrait (540×960)" },
    { width: 480, height: 640, label: "VGA Portrait (480×640)" }
];

export async function getWebcamTrackInfo(deviceId: string): Promise<WebcamTrackInfo> {
    const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { deviceId: { exact: deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } }
    });

    const track = stream.getVideoTracks()[0];
    const capabilities = track.getCapabilities?.() || null;
    const settings = track.getSettings();

    track.stop();

    return { capabilities, settings };
}

function checkDimensionsInRange(
    width: number,
    height: number,
    capabilities: MediaTrackCapabilities
): boolean {
    const { width: capWidth, height: capHeight } = capabilities;

    if (capWidth) {
        if (capWidth.min !== undefined && width < capWidth.min) return false;
        if (capWidth.max !== undefined && width > capWidth.max) return false;
    }

    if (capHeight) {
        if (capHeight.min !== undefined && height < capHeight.min) return false;
        if (capHeight.max !== undefined && height > capHeight.max) return false;
    }

    return true;
}

function isResolutionInRange(
    resolution: ResolutionOption,
    capabilities: MediaTrackCapabilities | null
): boolean {
    if (!capabilities) {
        // If no capabilities available, allow all resolutions
        return true;
    }

    // Only allow resolutions the camera can directly output
    // Don't allow portrait resolutions if camera only supports landscape (swapped)
    // This prevents showing portrait options that won't actually work
    return checkDimensionsInRange(resolution.width, resolution.height, capabilities);
}

function isCandidateInRange(
    candidate: ResolutionProbeCandidate,
    capabilities: MediaTrackCapabilities | null
): boolean {
    return isResolutionInRange(
        { width: candidate.width, height: candidate.height, label: "" },
        capabilities
    );
}

export function buildResolutionOptions(info: WebcamTrackInfo): ResolutionOption[] {
    const { capabilities, settings } = info;

    const filtered = CURATED_RESOLUTIONS.filter((res) => isResolutionInRange(res, capabilities));

    // Add current settings resolution if it's not already in the list
    const currentWidth = settings.width;
    const currentHeight = settings.height;

    if (currentWidth && currentHeight) {
        const alreadyIncluded = filtered.some(
            (res) => res.width === currentWidth && res.height === currentHeight
        );

        if (!alreadyIncluded) {
            filtered.push({
                width: currentWidth,
                height: currentHeight,
                label: `Current (${currentWidth}×${currentHeight})`
            });
        }
    }

    // Sort by resolution (descending by total pixels)
    filtered.sort((a, b) => b.width * b.height - a.width * a.height);

    return filtered;
}

export function findDefaultResolution(
    options: ResolutionOption[],
    existingResolution?: WebcamResolution | null
): ResolutionOption | null {
    if (options.length === 0) return null;

    // Prefer existing saved resolution if still valid
    if (existingResolution) {
        const existing = options.find(
            (opt) =>
                opt.width === existingResolution.width && opt.height === existingResolution.height
        );
        if (existing) return existing;
    }

    // Prefer 1280×720 if available
    const hd = options.find((opt) => opt.width === 1280 && opt.height === 720);
    if (hd) return hd;

    // Prefer 1920×1080 as second choice
    const fullHd = options.find((opt) => opt.width === 1920 && opt.height === 1080);
    if (fullHd) return fullHd;

    // Fall back to first option
    return options[0];
}

export function getDefaultResolutionProbeCandidates(): ResolutionProbeCandidate[] {
    // A small set of common sizes grouped by aspect ratio. This is intentionally not exhaustive.
    return [
        // 4:3 (landscape)
        { width: 320, height: 240, aspectRatio: "4:3" },
        { width: 640, height: 480, aspectRatio: "4:3" },
        { width: 800, height: 600, aspectRatio: "4:3" },
        { width: 1024, height: 768, aspectRatio: "4:3" },
        { width: 1280, height: 960, aspectRatio: "4:3" },
        { width: 1600, height: 1200, aspectRatio: "4:3" },

        // 16:9 (landscape)
        { width: 320, height: 180, aspectRatio: "16:9" },
        { width: 640, height: 360, aspectRatio: "16:9" },
        { width: 854, height: 480, aspectRatio: "16:9" },
        { width: 960, height: 540, aspectRatio: "16:9" },
        { width: 1280, height: 720, aspectRatio: "16:9" },
        { width: 1920, height: 1080, aspectRatio: "16:9" },

        // 1:1 (square)
        { width: 240, height: 240, aspectRatio: "1:1" },
        { width: 480, height: 480, aspectRatio: "1:1" },
        { width: 640, height: 640, aspectRatio: "1:1" },
        { width: 720, height: 720, aspectRatio: "1:1" },
        { width: 960, height: 960, aspectRatio: "1:1" },
        { width: 1080, height: 1080, aspectRatio: "1:1" },

        // 3:4 (portrait)
        { width: 240, height: 320, aspectRatio: "3:4" },
        { width: 480, height: 640, aspectRatio: "3:4" },
        { width: 600, height: 800, aspectRatio: "3:4" },
        { width: 768, height: 1024, aspectRatio: "3:4" },
        { width: 960, height: 1280, aspectRatio: "3:4" },

        // 9:16 (portrait)
        { width: 180, height: 320, aspectRatio: "9:16" },
        { width: 360, height: 640, aspectRatio: "9:16" },
        { width: 540, height: 960, aspectRatio: "9:16" },
        { width: 720, height: 1280, aspectRatio: "9:16" },
        { width: 1080, height: 1920, aspectRatio: "9:16" }
    ];
}

function pickProbeVariants(
    supportedConstraints: MediaTrackSupportedConstraints | null,
    capabilities: MediaTrackCapabilities | null
): ResolutionProbeVariant[] {
    const variants: ResolutionProbeVariant[] = ["default"];

    const supportsResizeMode = Boolean(supportedConstraints?.resizeMode);
    const supportsCropAndScale = capabilities?.resizeMode?.includes?.("crop-and-scale");
    if (supportsResizeMode && supportsCropAndScale) {
        variants.push("crop-and-scale");
    }

    return variants;
}

function toProbeActualSettings(settings: MediaTrackSettings): ResolutionProbeResult["actual"] {
    return {
        width: settings.width,
        height: settings.height,
        frameRate: settings.frameRate,
        resizeMode: settings.resizeMode,
        aspectRatio: settings.aspectRatio
    };
}

export async function probeWebcamResolutions(
    deviceId: string,
    candidates: ResolutionProbeCandidate[] = getDefaultResolutionProbeCandidates()
): Promise<{
    supportedConstraints: MediaTrackSupportedConstraints | null;
    capabilities: MediaTrackCapabilities | null;
    initialSettings: MediaTrackSettings;
    results: ResolutionProbeResult[];
}> {
    const supportedConstraints = navigator.mediaDevices.getSupportedConstraints?.() || null;

    const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
            deviceId: { exact: deviceId },
            // Encourage the browser to start as high as it reasonably can (not mandatory).
            width: { ideal: 1920 },
            height: { ideal: 1080 }
        }
    });

    const track = stream.getVideoTracks()[0];
    const capabilities = track.getCapabilities?.() || null;
    const initialSettings = track.getSettings();

    const variants = pickProbeVariants(supportedConstraints, capabilities);

    const filteredCandidates = candidates.filter((c) => isCandidateInRange(c, capabilities));

    const results: ResolutionProbeResult[] = [];

    for (const candidate of filteredCandidates) {
        for (const variant of variants) {
            const constraints: MediaTrackConstraints = {
                width: { exact: candidate.width },
                height: { exact: candidate.height }
            };

            if (variant === "crop-and-scale") {
                // Allow the browser to crop/scale to match the requested size (when supported).
                (constraints as any).resizeMode = "crop-and-scale";
            }

            try {
                await track.applyConstraints(constraints);
                const settings = track.getSettings();
                results.push({
                    width: candidate.width,
                    height: candidate.height,
                    aspectRatio: candidate.aspectRatio,
                    variant,
                    success: true,
                    actual: toProbeActualSettings(settings)
                });
            } catch (err: any) {
                results.push({
                    width: candidate.width,
                    height: candidate.height,
                    aspectRatio: candidate.aspectRatio,
                    variant,
                    success: false,
                    errorName: err?.name,
                    errorConstraint: err?.constraint,
                    errorMessage: err?.message
                });
            }
        }
    }

    // Stop stream tracks
    stream.getTracks().forEach((t) => t.stop());

    return { supportedConstraints, capabilities, initialSettings, results };
}

export function formatProbeResultsForDisplay(payload: {
    supportedConstraints: MediaTrackSupportedConstraints | null;
    capabilities: MediaTrackCapabilities | null;
    initialSettings: MediaTrackSettings;
    results: ResolutionProbeResult[];
}): string {
    const { supportedConstraints, capabilities, initialSettings, results } = payload;

    const lines: string[] = [];
    lines.push("Webcam resolution probe (applyConstraints)");
    lines.push("");
    lines.push(`Initial settings: ${initialSettings.width}×${initialSettings.height}`);
    if (initialSettings.frameRate !== undefined) {
        lines.push(`Initial fps: ${initialSettings.frameRate}`);
    }
    if (initialSettings.resizeMode) {
        lines.push(`Initial resizeMode: ${initialSettings.resizeMode}`);
    }
    lines.push("");

    lines.push("Supported constraints:");
    lines.push(JSON.stringify(supportedConstraints, null, 2));
    lines.push("");

    lines.push("Track capabilities:");
    lines.push(JSON.stringify(capabilities, null, 2));
    lines.push("");

    lines.push("Results:");
    for (const r of results) {
        const prefix = `${r.aspectRatio} ${r.width}×${r.height} [${r.variant}]`;
        if (r.success) {
            const actual = r.actual ? `${r.actual.width}×${r.actual.height}` : "unknown";
            const extra =
                r.actual?.resizeMode || r.actual?.frameRate
                    ? ` (resizeMode=${r.actual?.resizeMode || "?"}, fps=${
                          r.actual?.frameRate ?? "?"
                      })`
                    : "";
            lines.push(`${prefix} ✅ -> ${actual}${extra}`);
        } else {
            const constraint = r.errorConstraint ? ` constraint=${r.errorConstraint}` : "";
            lines.push(
                `${prefix} ❌ ${r.errorName || "Error"}${constraint} ${r.errorMessage || ""}`.trim()
            );
        }
    }

    return lines.join("\n");
}

// --------------------------------------------------------------------------
// Frame rate probing (per camera + resolution)
// --------------------------------------------------------------------------

export type FrameRateOption = { fps: number; label: string };

export type FrameRateProbeResult = {
    fps: number;
    success: boolean;
    actualFps?: number;
    errorName?: string;
    errorMessage?: string;
};

const CURATED_FPS_CANDIDATES = [1, 5, 10, 15, 20, 24, 30, 60] as const;

function isFpsInRange(fps: number, capabilities: MediaTrackCapabilities | null): boolean {
    if (!capabilities?.frameRate) return true;

    const { min, max } = capabilities.frameRate;
    if (min !== undefined && fps < min) return false;
    if (max !== undefined && fps > max) return false;

    return true;
}

function fpsMatchesWithTolerance(actual: number, requested: number, tolerance = 0.5): boolean {
    return Math.abs(actual - requested) <= tolerance;
}

export async function probeWebcamFrameRates(
    deviceId: string,
    resolution: WebcamResolution,
    fpsCandidates: readonly number[] = CURATED_FPS_CANDIDATES
): Promise<{ capabilities: MediaTrackCapabilities | null; results: FrameRateProbeResult[] }> {
    const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { deviceId: { exact: deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } }
    });

    const track = stream.getVideoTracks()[0];
    const capabilities = track.getCapabilities?.() || null;

    // Lock width/height first (same approach as resolution probing)
    const supported = navigator.mediaDevices.getSupportedConstraints?.() as any;
    const caps = track.getCapabilities?.() as any;
    const canCropAndScale =
        Boolean(supported?.resizeMode) && Boolean(caps?.resizeMode?.includes?.("crop-and-scale"));

    const resolutionConstraints: MediaTrackConstraints = {
        width: { exact: resolution.width },
        height: { exact: resolution.height }
    };
    if (canCropAndScale) {
        (resolutionConstraints as any).resizeMode = "crop-and-scale";
    }

    try {
        await track.applyConstraints(resolutionConstraints);
    } catch {
        // Resolution not achievable — skip FPS probing and return empty results
        stream.getTracks().forEach((t) => t.stop());
        return { capabilities, results: [] };
    }

    // Filter candidates by capability range
    const candidates = [...fpsCandidates].filter((fps) => isFpsInRange(fps, capabilities));

    const results: FrameRateProbeResult[] = [];

    for (const fps of candidates) {
        const constraints: MediaTrackConstraints = {
            width: { exact: resolution.width },
            height: { exact: resolution.height },
            frameRate: { exact: fps }
        };
        if (canCropAndScale) {
            (constraints as any).resizeMode = "crop-and-scale";
        }

        try {
            await track.applyConstraints(constraints);
            const actualFps = track.getSettings().frameRate;
            const success = actualFps !== undefined && fpsMatchesWithTolerance(actualFps, fps);
            results.push({ fps, success, actualFps });
        } catch (err: any) {
            results.push({ fps, success: false, errorName: err?.name, errorMessage: err?.message });
        }
    }

    stream.getTracks().forEach((t) => t.stop());

    return { capabilities, results };
}

export function buildFrameRateOptions(
    results: FrameRateProbeResult[],
    capabilities: MediaTrackCapabilities | null
): FrameRateOption[] {
    const successful = results.filter((r) => r.success);

    if (successful.length === 0) {
        // Fallback: return range from capabilities or a sensible default
        const maxFps = capabilities?.frameRate?.max ?? 30;
        return [{ fps: Math.min(30, maxFps), label: `${Math.min(30, maxFps)} fps` }];
    }

    return successful
        .sort((a, b) => a.fps - b.fps)
        .map((r) => ({ fps: r.fps, label: `${r.fps} fps` }));
}

export function findDefaultFrameRate(
    options: FrameRateOption[],
    existingFrameRate?: number | null
): FrameRateOption | null {
    if (options.length === 0) return null;

    // Prefer existing saved frame rate if still valid
    if (existingFrameRate != null) {
        const existing = options.find((opt) => opt.fps === existingFrameRate);
        if (existing) return existing;
    }

    // Prefer 30 fps if available
    const fps30 = options.find((opt) => opt.fps === 30);
    if (fps30) return fps30;

    // Prefer 15 fps as second choice
    const fps15 = options.find((opt) => opt.fps === 15);
    if (fps15) return fps15;

    // Fall back to highest available
    return options[options.length - 1];
}
